/**
 * compliance — «Комплаенс»: recordingDisclaimer / tcpaCompliance / dataPrivacy
 * (три состояния — пройдено / не пройдено / не проверялось) и crmCompleteness
 * (вердикт + балл + недостающие поля). Зеркалит ComplianceSection.tsx:
 * порядок строк и три состояния (`complianceVerdict`) — буквально те же;
 * `applicable === false` скрывает строку целиком, как на экране.
 * `crmCompleteness.passed` в типе не nullable (только true/false) — третье
 * состояние ему не нужно, и на экране у него его тоже нет.
 *
 * `tcpaCompliance` печатается — в старом printReport.ts эта строка
 * отсутствовала вовсе, хотя на экране есть; это одно из расхождений, ради
 * которых затевалась вся работа над печатным отчётом (task-8-brief.md).
 * Согласованный прототип (preview.html:253-255) её в иллюстративном примере
 * не показывает — но `.chk`-разметка одинакова для всех проверок, добавление
 * четвёртой строки не требует новой вёрстки.
 *
 * `quote`/`reason` отдельных проверок НЕ печатаются — бриф задачи 8 их не
 * упоминает в списке экранируемых полей, и согласованный прототип тоже не
 * показывает ничего, кроме иконки-вердикта и названия проверки.
 *
 * Балл CRM в шапке карточки (`.val`, как в content.ts/sale-probability.ts)
 * сознательно не печатается: согласованный прототип (preview.html:251) для
 * этой карточки шапку без `.val` рисует, а сам балл уже виден в теле
 * (строка crmCompleteness, `.d`) — печатать его дважды незачем.
 *
 * У организации с Э2 встроенных (#2518) строки уведомления, TCPA и
 * конфиденциальности печатаются только при результате ключа по старому полю —
 * то же правило, что на экране (`lib/records/builtin-legacy-rows`): иначе
 * встроенный стоит в разделе «Критерии».
 */
import type { BuiltinCriterionKey } from '@aica/core/call-criteria';
import { pausedByAeloRow, showsLegacyRow } from '@/lib/records/builtin-legacy-rows';
import { escapeHtml } from '../escape';
import { card, safeNumber } from '../primitives';
import { scoreHex } from '../tokens';
import type { SectionFn, TFn } from '../types';

type VerdictClass = 'ok' | 'no' | 'na';

function verdict(passed: boolean | null | undefined): {
  icon: string;
  cls: VerdictClass;
} {
  // == null тоже ловит undefined — как в ComplianceSection.tsx,
  // «неизмеренное» состояние трактуется одинаково для обоих.
  if (passed == null) return { icon: '—', cls: 'na' };
  return passed ? { icon: '✓', cls: 'ok' } : { icon: '✗', cls: 'no' };
}

/**
 * Хинт «не проверялось» — печатный текст, которого на экране НЕТ (там для
 * `passed == null` печатается голое «—»). На экране рядом видна вся панель и
 * настройки промпт-профиля, пользователь понимает, что правило было
 * выключено или неприменимо. PDF читают вне приложения, часто руководитель
 * или аудитор — без подписи голый прочерк в комплаенс-строке легко прочитать
 * как «проверка провалена» или «данные потеряны», а не как «правило не
 * запускалось». Тот же довод и тот же выбор, что для `closingNotApplicable`
 * в dialogue.ts (задача 7). НЕ убирать подпись «ради единообразия с экраном»
 * — в комплаенсе цена такой правки выше, чем где-либо ещё в отчёте.
 */
function chkRow(
  passed: boolean | null | undefined,
  labelText: string,
  t: TFn,
  note?: string
): string {
  const v = verdict(passed);
  const hint =
    (v.cls === 'na'
      ? ` <span class="d">${escapeHtml(t('records.analysisPanel.compliance.notChecked'))}</span>`
      : '') + (note ? ` <span class="d">${escapeHtml(note)}</span>` : '');
  return `<div class="chk"><span class="i ${v.cls}">${v.icon}</span><span>${escapeHtml(labelText)}</span>${hint}</div>`;
}

export const renderCompliance: SectionFn = (input, t) => {
  const compliance = input.analysis.result.compliance;
  if (!compliance) return '';
  const legacy = (key: BuiltinCriterionKey) =>
    showsLegacyRow(input.builtinSources, key, input.builtinPausedByAelo);
  // Подпись паузы Aelo — как на экране (`ComplianceSection`).
  const pausedNote = (key: BuiltinCriterionKey) =>
    pausedByAeloRow(input.builtinSources, key, input.builtinPausedByAelo)
      ? t('records.analysisPanel.compliance.pausedByAelo')
      : undefined;

  const rows: string[] = [];

  if (
    legacy('recording_disclosed') &&
    compliance.recordingDisclaimer &&
    compliance.recordingDisclaimer.applicable !== false
  ) {
    rows.push(
      chkRow(
        compliance.recordingDisclaimer.passed,
        t('records.analysisPanel.recordingDisclaimer'),
        t,
        pausedNote('recording_disclosed')
      )
    );
  }

  if (
    legacy('tcpa_compliant') &&
    compliance.tcpaCompliance &&
    compliance.tcpaCompliance.applicable !== false
  ) {
    rows.push(
      chkRow(
        compliance.tcpaCompliance.passed,
        t('records.analysisPanel.tcpaCompliance'),
        t,
        pausedNote('tcpa_compliant')
      )
    );
  }

  if (
    legacy('data_privacy_violation') &&
    compliance.dataPrivacy &&
    compliance.dataPrivacy.applicable !== false
  ) {
    rows.push(
      chkRow(
        compliance.dataPrivacy.passed,
        t('records.analysisPanel.dataPrivacy'),
        t,
        pausedNote('data_privacy_violation')
      )
    );
  }

  if (compliance.crmCompleteness) {
    const crm = compliance.crmCompleteness;
    const v = verdict(crm.passed);
    // `crm.score` — приведение типа, не гарантия (см. `safeNumber` в
    // primitives.ts); нечисловое значение опускается, а не подставляется.
    const crmScore = safeNumber(crm.score);
    const scoreHtml =
      crmScore !== null
        ? ` <span class="d" style="color:${scoreHex(crmScore)}">${crmScore}</span>`
        : '';
    const missingHtml =
      crm.missingFields && crm.missingFields.length > 0
        ? `<ul>${crm.missingFields.map((f) => `<li>${escapeHtml(f)}</li>`).join('')}</ul>`
        : '';
    rows.push(
      `<div class="chk"><span class="i ${v.cls}">${v.icon}</span><span>${escapeHtml(
        t('records.analysisPanel.crmCompleteness')
      )}</span>${scoreHtml}</div>${missingHtml}`
    );
  }

  if (rows.length === 0) return '';

  return card({
    title: t('records.analysisPanel.compliance.title'),
    body: rows.join(''),
  });
};
